import React, { forwardRef, useCallback, useContext, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { getLocalSettingsProxy, isoStringDisplay } from 'utilities';
import { getHighlightProps } from 'utilities/componentUtilities';
import { OptionsMenu } from 'components';
import {
  LabelForUserContentItems,
  UserContentCard,
} from 'components/UserContentCards';
import { UserContentItemModifiersContext } from 'contexts';

const useStyles = makeStyles((theme) => {
  return {
    root: {},

    highlight: ({ highlightProps }) => {
      if (highlightProps) {
        return {
          backgroundColor: highlightProps.background,
          outlineColor: highlightProps.border,
          outlineWidth: '2px',
          outlineStyle: 'solid',
        };
      } else {
        return 'none';
      }
    },
    noteContainer: {},
    noteAvatarContainer: {},
    noteTextarea: {},
  };
});

const HighlightCard = forwardRef(
  ({ className, userContentItem, ...props }, ref) => {
    const { bookLocation, color, dateCreated, page, id, truncatedSnippet } =
      userContentItem;

    const { focusInlineHighlight, setAriaLiveMessageForUserContent } =
      useContext(UserContentItemModifiersContext);

    const [localSettings] = useState(getLocalSettingsProxy);
    const paletteId = localSettings.appStyling.colorPalette;
    const highlightProps = getHighlightProps({ color, paletteId });

    const classes = useStyles({
      highlightProps,
    });

    const deleteOnClick = useCallback(async () => {
      // Find the element to focus before deletion
      const currentCard = ref?.current;
      let elementToFocus = null;

      if (currentCard) {
        // Get the parent <li> element
        const listItem = currentCard.closest('li');
        if (listItem) {
          // Only set focus if this is the last element in the list
          if (!listItem.nextElementSibling && listItem.previousElementSibling) {
            // Find the focusable element within the previous sibling
            elementToFocus =
              listItem.previousElementSibling.querySelector('[tabindex]') ||
              listItem.previousElementSibling.querySelector('button');
          }
        }
      }

      await userContentItem.delete();

      // Focus the element after deletion
      if (elementToFocus) {
        elementToFocus.focus();
      }

      setTimeout(() => {
        setAriaLiveMessageForUserContent({
          action: 'deleted',
          color: userContentItem.color,
          snippet: userContentItem.truncatedSnippet,
          type: userContentItem.constructor.typeName,
        });
      }, 2000);
    }, [ref, setAriaLiveMessageForUserContent, userContentItem]);

    const optionsMenuItemsList = [
      {
        displayText: 'Delete Highlight',
        onClick: deleteOnClick,
        key: 'delete-highlight',
      },
    ];

    const cardId = `highlight-card-${id}`;
    const title = page?.label ? (
      <>
        Page <span className="noTranslate">{page.label}</span>
      </>
    ) : (
      ''
    );

    return (
      <UserContentCard
        ref={ref}
        id={cardId}
        action={
          <OptionsMenu
            cardId={cardId}
            menuItemsList={optionsMenuItemsList}
            userContentItem={userContentItem}
          />
        }
        bookLocation={bookLocation}
        classNames={{
          root: clsx(classes.root, className),
          highlight: classes.highlight,
        }}
        linkLabel={
          <LabelForUserContentItems userContentItem={userContentItem} />
        }
        onSnippetClick={() => focusInlineHighlight(userContentItem)}
        snippet={truncatedSnippet}
        subheader={
          <span className="noTranslate">{isoStringDisplay(dateCreated)}</span>
        }
        title={title}
        {...props}
      />
    );
  },
);

HighlightCard.propTypes = {
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
};

export default HighlightCard;
